import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@apis/index";
import Routes from "./routes";
import { CartTypeRequest } from "@scenes/searchResults/Utils";
import { CartItemsResult } from "@src/types/CartItemResult";
import { RemoveFromBulkCart } from "@src/types/RemoveFromBulkCart";
import { BulkRequestEnum } from "@utils/constants";
import {
  CartItemListResponse,
  CheckItemAvailabilityResponse,
  StockAvailabilityResponse,
} from "@src/types/CartItemListResponse";
import {
  setOrderCartResponse,
  setShippingMethods,
} from "@redux/slices/orderCartSlice";
import { useDispatch } from "react-redux";
import { StoneType } from "@scenes/bulk-request/Utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useConsolidatedStoneTypes } from "@redux/slices/globalSlice";

export const useAddToCart = (type: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { stock_items_cart: CartTypeRequest }) =>
      apiClient.post<CartItemsResult>(
        Routes.ADD_TO_BULK_REQUEST + "?minimal_response=true",
        data
      ),
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["UNIFIED_BULK_REQUEST"] })
        .then();
      switch (type) {
        //Todo --uncomment this once we add functionality of bulk items on favorites.
        // case BulkRequestEnum.FROMRESULTSCREEN:
        //   queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
        //   queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
        //   break;
        // case BulkRequestEnum.FROMFAVORITES:
        //   queryClient.invalidateQueries({ queryKey: ["SEARCH_RESULTS"] }).then();
        //   break;
        case BulkRequestEnum.FromPdp:
          queryClient
            .invalidateQueries({ queryKey: ["SEARCH_RESULTS"] })
            .then();
          // queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
          // queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
          break;
      }
    },
  });
};

export const useRemoveFromCart = (type: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { stock_items_cart: CartTypeRequest }) =>
      apiClient.delete<RemoveFromBulkCart>(
        Routes.REMOVE_FROM_BULK_REQUEST + "?minimal_response=true",
        {
          data: data.stock_items_cart,
        }
      ),
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["UNIFIED_BULK_REQUEST"] })
        .then();
      switch (type) {
        //Todo --uncomment this once we add functionality of bulk items on favorites.
        // case BulkRequestEnum.FromResultScreen:
        //   queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
        //   queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
        //   return;
        // case BulkRequestEnum.FromFavorites:
        //   queryClient.invalidateQueries({ queryKey: ["SEARCH_RESULTS"] }).then();
        //   return;
        case BulkRequestEnum.FromPdp:
          queryClient
            .invalidateQueries({ queryKey: ["SEARCH_RESULTS"] })
            .then();
          // queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
          // queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
          return;
      }
    },
  });
};

export const useGetCartItems = (
  stockType: string,
  cartType: string,
  priceMode: number,
  userMarkup: any,
  enabled: boolean,
  coupon_code?: string,
  selectedStoneType?: StoneType,
  userCurrency?: any,
  buyNowSessionId?: string,
  setSelectedItems?: any,
  productSubType?: StoneType
) => {
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const consolidatedStoneTypes = useConsolidatedStoneTypes();
  const queryKey = [
    "GET_CART_ITEM",
    stockType,
    priceMode,
    userMarkup,
    ...(selectedStoneType !== StoneType.SINGLE_STONES
      ? [selectedStoneType]
      : []),
    userCurrency,
    userInfo?.user?.id,
    buyNowSessionId,
    productSubType,
  ];
  return useQuery({
    queryKey: queryKey,
    enabled: enabled,
    queryFn: () =>
      apiClient
        .get<CartItemListResponse>(Routes.GET_CART_ITEMS, {
          params: {
            stock_type: stockType,
            cart_type: cartType,
            price_mode: priceMode,
            ...(buyNowSessionId ? { buy_now_session_id: buyNowSessionId } : {}),
            ...(selectedStoneType !== StoneType.SINGLE_STONES && {
              product_sub_type: consolidatedStoneTypes?.enabled
                ? "all"
                : selectedStoneType === StoneType.PARCELS
                ? "parcel"
                : productSubType
                ? productSubType
                : selectedStoneType,
            }),
            ...(coupon_code ? { coupon_code } : {}),
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        })
        .then((response) => {
          if (setSelectedItems) {
            setSelectedItems([]);
          }
          const data = response?.data.response ?? [];
          dispatch(
            setOrderCartResponse({
              ...data,
              buy_now_session_id: buyNowSessionId ?? null,
            })
          );

          dispatch(setOrderCartResponse(data as any));
          return response;
        }),
  });
};

export const useCheckCoupon = (
  cartType: string,
  priceMode: number,
  coupon_code: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ["CHECK_COUPON"],
    enabled: enabled,
    queryFn: () =>
      apiClient.get<CartItemListResponse>(Routes.GET_CART_ITEMS, {
        params: {
          stock_type: "Jewelry",
          cart_type: cartType,
          price_mode: priceMode,
          ...(coupon_code ? { coupon_code } : {}),
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useMarkCartSold = () => {
  return useMutation({
    mutationFn: (data: {
      ids: number[];
      cart_type: string;
      stock_type: string;
      price_mode: string;
    }) => apiClient.put(Routes.MARK_CART_SOLD, data),
  });
};

export const useDeleteCartItem = () => {
  return useMutation({
    mutationFn: ({
      id,
      cartType,
      priceMode,
      stockType,
    }: {
      id: string;
      cartType: string;
      priceMode: number;
      stockType?: string;
    }) =>
      apiClient.delete<CartItemListResponse>(
        Routes.REMOVE_FROM_ORDER_CART(id),
        {
          params: {
            stock_type: stockType,
            cart_type: cartType,
            price_mode: priceMode,
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });
};

export const useUpdateItemQuantity = () => {
  return useMutation({
    mutationFn: ({
      cartType,
      priceMode,
      id,
      quantity,
    }: {
      cartType: string;
      priceMode: number;
      id: string;
      quantity: number;
    }) =>
      apiClient.put<CartItemListResponse>(Routes.REMOVE_FROM_ORDER_CART(id), {
        stock_type: "Jewelry",
        cart_type: cartType,
        price_mode: priceMode,
        quantity,
      }),
  });
};

export const useOrderCheckout = () => {
  return useMutation({
    mutationFn: (data) => apiClient.post(Routes.ORDER_CHECKOUT, data),
  });
};

export const useGetShippingMethods = (enabled: boolean) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["GET_SHIPPING_METHODS"],
    enabled: !!enabled,
    queryFn: () =>
      apiClient
        .get(Routes.GET_SHIPPING_METHODS, {
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        })
        .then((response) => {
          dispatch(setShippingMethods(response.data));
          return response;
        }),
  });
};

export const usePayPalOrderCheckout = () => {
  return useMutation({
    mutationFn: (data: {
      order: {
        order_id: string;
        amount: string;
        payment_method_nonce: string;
        customer: any;
        shipping: any;
        paypal_checkout_currency: string;
        paypal_checkout_amount: string;
        order_comments?: string;
      };
    }) => apiClient.post(Routes.ORDERS, data),
  });
};

export const usePayPalCaptureTransaction = () => {
  return useMutation({
    mutationFn: (data: {
      order_id: string;
      payment_method: string;
      order_comments: string;
      total_price: string;
      shipping_method?: string;
      cart_subtotal?: number;
      front_calculation_price?: number;
      shipping_location?: any;
      shipping_instructions?: boolean;
      wire_transfer_details?: boolean;
      promo_discount_amount?: number;
      promo_code_label?: string;
    }) => apiClient.post(Routes.PAYPAL_CAPTURE_TRANSACTION, data),
  });
};

export const useOfflinePayment = () => {
  return useMutation({
    mutationFn: (data: {
      orderID: string;
      total_price: string;
      payment_mode: string;
      order_comments?: string;
      shipping_method?: string;
      cart_subtotal?: number;
      front_calculation_price?: number;
      shipping_location?: any;
      shipping_instructions?: boolean;
      wire_transfer_details?: boolean;
      promo_discount_amount?: number;
      promo_code_label?: string;
    }) => apiClient.post(Routes.OFFLINE_PAYMENT, data),
  });
};

export const useCheckItemAvailability = () => {
  return useMutation({
    mutationFn: (data: { stock_item_ids: number[] }) =>
      apiClient.post<CheckItemAvailabilityResponse>(
        Routes.CHECK_ITEM_AVAILABILITY,
        data
      ),
  });
};

export const useCheckItemAvailabilityWithSupplier = () => {
  return useMutation({
    mutationFn: (data: { stock_num: string[] }) =>
      apiClient.post<StockAvailabilityResponse>(
        Routes.CHECK_STORE_AVAILABILITY,
        data
      ),
  });
};

export const useUpdateBulkCartQuantity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      itemId: string;
      cart_type: string;
      price_mode: number;
      quantity: number;
      stock_type: string;
    }) =>
      apiClient.put<CartItemsResult>(
        `${Routes.REMOVE_FROM_ORDER_CART(data.itemId)}?minimal_response=true`,
        {
          cart_type: data.cart_type,
          price_mode: data.price_mode,
          quantity: data.quantity,
          stock_type: data.stock_type,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GET_CART_ITEM"] }).then();
    },
  });
};
